import { jsx } from "react/jsx-runtime";
import { css, cx } from "@/styled-system/css";
import { Box, Form, AdornedInput } from "@zillow/constellation-10";
import { IconSearchFilled } from "@zillow/constellation-icons";
import { useState, useRef, useId, useEffect, useLayoutEffect } from "react";
import { FONT_FAMILY_UI } from "../_constellationUiTokens.esm.js";
const MOBILE_COLLAPSE_MQ = "(max-width: 768px)";
const useNarrowForCollapsibleSearch = () => {
  const [narrow, setNarrow] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia(MOBILE_COLLAPSE_MQ);
    const handler = () => setNarrow(mq.matches);
    setNarrow(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return narrow;
};
const mobileCollapsedOuter = css({
  minWidth: "0",
  maxWidth: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexShrink: 0
});
const mobileSearchCollapsedTrigger = css({
  appearance: "none",
  WebkitAppearance: "none",
  margin: "0",
  padding: "0",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: "0",
  width: "40px",
  height: "40px",
  borderRadius: "9999px",
  borderWidth: "{borderWidths.25}",
  borderStyle: "solid",
  borderColor: "border.muted",
  backgroundColor: "white",
  boxSizing: "border-box",
  fontFamily: FONT_FAMILY_UI,
  _hover: {
    borderColor: "gray.400"
  },
  _focusVisible: {
    outlineStyle: "solid",
    outlineWidth: "2px",
    outlineOffset: "2px",
    outlineColor: "blue.500"
  }
});
const mobileExpandedShell = css({
  position: "relative",
  flex: "1 1 0%",
  minWidth: "0",
  width: "100%",
  maxWidth: "none",
  boxSizing: "border-box",
  overflow: "visible"
});
const searchPillShell = css({
  height: "40px",
  borderRadius: "9999px",
  overflow: "hidden",
  borderWidth: "{borderWidths.25}",
  borderStyle: "solid",
  borderColor: "border.muted",
  backgroundColor: "white",
  boxShadow: "none",
  outline: "none",
  _hover: {
    borderColor: "gray.400"
  },
  _focusWithin: {
    borderRadius: "9999px",
    boxShadow: "none"
  }
});
const searchAdornedDesktopRoot = css({
  width: "300px"
});
const searchAdornedMobileExpanded = css({
  position: "relative",
  width: "100%",
  minWidth: "0",
  maxWidth: "100%",
  backgroundColor: "white"
});
const searchFieldWrapMobileOpen = css({
  position: "relative",
  width: "100%",
  minWidth: "0",
  maxWidth: "100%",
  minHeight: "40px",
  paddingTop: "spacing.250",
  boxSizing: "border-box"
});
const mobileExpandedForm = css({
  display: "block",
  width: "100%",
  minWidth: "0",
  maxWidth: "100%",
  boxSizing: "border-box"
});
const searchAdornmentShell = css({
  backgroundColor: "transparent",
  border: "none",
  flexShrink: 0,
  paddingInlineEnd: "tighter"
});
const desktopSearchSlot = css({
  flexShrink: 0,
  xl: {
    paddingRight: "default"
  }
});
const searchAdornmentIconSlot = css({
  lineHeight: "1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: "16px",
  height: "16px"
});
const searchIconGlyph = css({
  color: "text.subtle"
});
const searchInputInner = css({
  fontFamily: FONT_FAMILY_UI,
  /** Explicit px font size and line height for the search field. */
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: 400,
  backgroundColor: "transparent",
  border: "none",
  borderWidth: "0",
  boxShadow: "none",
  color: "text.neutral",
  _placeholder: {
    color: "text.subtle",
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400
  },
  _active: {
    backgroundColor: "transparent"
  },
  _focus: {
    backgroundColor: "transparent"
  },
  _focusVisible: {
    backgroundColor: "transparent",
    boxShadow: "none",
    outline: "none"
  }
});
const DesktopSearchField = ({
  placeholder,
  actionPrefix
}) => /* @__PURE__ */ jsx(Form, { action: `${actionPrefix}`, method: "get", children: /* @__PURE__ */ jsx(AdornedInput, { title: "Search", fluid: false, size: "sm", className: cx(searchPillShell, searchAdornedDesktopRoot), startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { className: searchAdornmentShell, children: /* @__PURE__ */ jsx(AdornedInput.Icon, { className: searchAdornmentIconSlot, children: /* @__PURE__ */ jsx(IconSearchFilled, { className: searchIconGlyph }) }) }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { name: "s", placeholder, "aria-label": "Search", className: searchInputInner }) }) });
const MobileExpandedSearch = ({
  placeholder,
  actionPrefix,
  panelId,
  expandedShellRef,
  inputRef
}) => /* @__PURE__ */ jsx(Box, { ref: expandedShellRef, className: mobileExpandedShell, children: /* @__PURE__ */ jsx(Form, { action: `${actionPrefix}`, method: "get", id: panelId, className: mobileExpandedForm, children: /* @__PURE__ */ jsx(Box, { className: searchFieldWrapMobileOpen, role: "search", children: /* @__PURE__ */ jsx(AdornedInput, { title: "Search", fluid: true, size: "sm", className: cx(searchPillShell, searchAdornedMobileExpanded), startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { className: searchAdornmentShell, children: /* @__PURE__ */ jsx(AdornedInput.Icon, { className: searchAdornmentIconSlot, children: /* @__PURE__ */ jsx(IconSearchFilled, { className: searchIconGlyph }) }) }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { ref: inputRef, name: "s", placeholder, "aria-label": "Search", "aria-expanded": true, className: searchInputInner }) }) }) }) });
const SearchBox = ({
  placeholder = "Search Blog",
  actionPrefix = "/search",
  onMobileSearchOpenChange
}) => {
  const narrow = useNarrowForCollapsibleSearch();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const expandedShellRef = useRef(null);
  const inputRef = useRef(null);
  const searchPanelId = useId();
  useEffect(() => {
    if (!narrow) {
      setMobileSearchOpen(false);
    }
  }, [narrow]);
  useEffect(() => {
    onMobileSearchOpenChange == null ? void 0 : onMobileSearchOpenChange(Boolean(narrow && mobileSearchOpen));
  }, [narrow, mobileSearchOpen, onMobileSearchOpenChange]);
  useEffect(() => {
    if (!narrow || !mobileSearchOpen) {
      return;
    }
    const closeIfOutside = (event) => {
      const shell = expandedShellRef.current;
      if (shell && !shell.contains(event.target)) {
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeIfOutside, true);
    return () => document.removeEventListener("pointerdown", closeIfOutside, true);
  }, [narrow, mobileSearchOpen]);
  useEffect(() => {
    if (narrow && mobileSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [narrow, mobileSearchOpen]);
  if (!narrow) {
    return /* @__PURE__ */ jsx(Box, { className: desktopSearchSlot, children: /* @__PURE__ */ jsx(DesktopSearchField, { placeholder, actionPrefix }) });
  }
  if (!mobileSearchOpen) {
    return /* @__PURE__ */ jsx(Box, { className: mobileCollapsedOuter, children: /* @__PURE__ */ jsx("button", { type: "button", className: mobileSearchCollapsedTrigger, "aria-label": "Open search", "aria-expanded": false, "aria-controls": searchPanelId, onClick: () => setMobileSearchOpen(true), children: /* @__PURE__ */ jsx("span", { className: searchAdornmentIconSlot, "aria-hidden": true, children: /* @__PURE__ */ jsx(IconSearchFilled, { className: searchIconGlyph }) }) }) });
  }
  return /* @__PURE__ */ jsx(MobileExpandedSearch, { placeholder, actionPrefix, panelId: searchPanelId, expandedShellRef, inputRef });
};
export {
  SearchBox as default
};
